import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // 10 minutes
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If token expired and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        // Let the component handle the authentication error
        // Don't redirect here, let each component decide what to do
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  register: (email, password, name) =>
    api.post('/api/auth/register', { email, password, name }),

  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),

  logout: (refreshToken) =>
    api.post('/api/auth/logout', { refresh_token: refreshToken }),

  verifyEmail: (token) =>
    api.post('/api/auth/verify-email', { token }),

  resendVerification: (email) =>
    api.post('/api/auth/resend-verification', { email }),

  forgotPassword: (email) =>
    api.post('/api/auth/forgot-password', { email }),

  resetPassword: (token, newPassword) =>
    api.post('/api/auth/reset-password', { token, new_password: newPassword }),

  getProfile: () =>
    api.get('/api/profile'),

  getGoogleLoginUrl: () =>
    api.get('/api/auth/google'),
};

// Upload API calls
export const uploadAPI = {
  getPresignedUrl: (fileName, contentType, imageType = 'other') =>
    api.post('/api/upload/presigned-url', { 
      file_name: fileName, 
      content_type: contentType,
      image_type: imageType
    }),

  uploadToS3: async (presignedUrl, file) => {
    return axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  },

  confirmUpload: (fileKey, fileName, contentType, imageType, name = '') =>
    api.post('/api/upload/confirm', {
      file_key: fileKey,
      file_name: fileName,
      content_type: contentType,
      image_type: imageType,
      name: name,
    }),
};

// Image API calls
export const imageAPI = {
  listShirts: () =>
    api.get('/api/images/shirts'),
};

// Shirt Product API calls
export const shirtProductAPI = {
  listProducts: () =>
    api.get('/api/shirt-products'),

  getProduct: (uuid) =>
    api.get(`/api/shirt-products/${uuid}`),

  createProduct: (productData) =>
    api.post('/api/shirt-products', productData),

  updateProduct: (uuid, productData) =>
    api.put(`/api/shirt-products/${uuid}`, productData),

  deleteProduct: (uuid) =>
    api.delete(`/api/shirt-products/${uuid}`),

  listAllProducts: () =>
    api.get('/api/admin/shirt-products'),

  uploadProductImage: (uploadData) =>
    api.post('/api/admin/shirt-products/upload', uploadData),

  syncFromUploadedImages: () =>
    api.post('/api/admin/shirt-products/sync'),
};

// Generation API calls
export const generationAPI = {
  generate: (uploadedImageUUID, shirtProductUUID, sceneImageUUID, size = '4K', backgroundTheme = '', customBackground = '', personalization = null) =>
    api.post('/api/generate', {
      uploaded_image_uuid: uploadedImageUUID,
      shirt_product_uuid: shirtProductUUID,
      scene_image_uuid: sceneImageUUID,
      logo_image_uuid: personalization?.logoUUID || '',
      background_theme: backgroundTheme,
      custom_background: customBackground,
      personal_names: personalization?.names || '',
      custom_text: personalization?.customText || '',
      shirt_color: personalization?.color || '',
      size: size,
    }),

  getImageStatus: (imageId) =>
    api.get(`/api/image/${imageId}`),

  getVideoStatus: (taskId) =>
    api.get(`/api/video/${taskId}`),

  listVideos: () =>
    api.get('/api/videos'),

  getPublicVideo: (videoId) =>
    axios.get(`${API_BASE_URL}/api/public/video/${videoId}`),
};

export default api;
